import React, { useEffect, useState } from 'react';
import '../styles/NewsTab.css';

const NewsTab = ({ companyId, apiUrl }) => {
    const [news, setNews] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchNews = async () => {
            setLoading(true);
            try {
                const res = await fetch(`${apiUrl}/news/${companyId}`);
                const data = await res.json();
                setNews(data);
            } catch (err) {
                console.error('Error fetching news:', err);
            } finally {
                setLoading(false);
            }
        };

        if (companyId) {
            fetchNews();
        }
    }, [companyId, apiUrl]);

    if (loading) return <div className="news-loading">Loading news...</div>;
    if (!news || !news.articles || news.articles.length === 0) {
        return <div className="news-empty">No recent news available for {companyId}</div>;
    }

    const getSentimentColor = (sentiment) => {
        switch (sentiment) {
            case 'positive': return 'green';
            case 'negative': return 'red';
            default: return 'gray';
        }
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return 'Date unavailable';

        try {
            // Handle MongoDB datetime format or ISO string
            const date = new Date(dateString);

            // Check if date is valid
            if (isNaN(date.getTime())) {
                return 'Date unavailable';
            }

            const now = new Date();
            const diffMs = now - date;
            const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
            const diffDays = Math.floor(diffHours / 24);

            if (diffHours < 1) {
                const diffMins = Math.floor(diffMs / (1000 * 60));
                return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
            } else if (diffHours < 24) {
                return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
            } else if (diffDays < 7) {
                return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
            } else {
                return date.toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                });
            }
        } catch (error) {
            console.error('Error formatting date:', dateString, error);
            return 'Date unavailable';
        }
    };

    const formatLastUpdated = (dateString) => {
        if (!dateString) return 'Not available';

        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) {
                return 'Not available';
            }
            return date.toLocaleString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch (error) {
            console.error('Error formatting lastUpdated:', dateString, error);
            return 'Not available';
        }
    };

    return (
        <div className="news-container">
            <div className="news-header">
                <h2>Latest News: {news.companyName}</h2>
                <p className="news-updated">
                    Last updated: {formatLastUpdated(news.lastUpdated)}
                </p>
            </div>

            {news.overallSummary && (
                <div className="news-overall-summary">
                    <h3>📰 Today's Summary</h3>
                    <p>{news.overallSummary}</p>
                    {news.keyTopics && news.keyTopics.length > 0 && (
                        <div className="news-topics">
                            {news.keyTopics.map((topic, idx) => (
                                <span key={idx} className="topic-tag">{topic}</span>
                            ))}
                        </div>
                    )}
                </div>
            )}

            <div className="news-articles">
                {news.articles.map((article, idx) => (
                    <div key={idx} className="news-card">
                        <div className="news-card-content">
                            <div className="news-card-header">
                                <span
                                    className="sentiment-badge"
                                    style={{ backgroundColor: getSentimentColor(article.sentiment) }}
                                >
                                    {article.sentiment.toUpperCase()}
                                </span>
                                <span className="news-source">{article.source}</span>
                            </div>
                            <div className="news-card-body">
                                {article.urlToImage && (
                                    <div className="news-thumbnail">
                                        <img
                                            src={article.urlToImage}
                                            alt={article.title}
                                            onError={(e) => { e.target.style.display = 'none'; }}
                                        />
                                    </div>
                                )}
                                <div className="news-text-content">
                                    <h3 className="news-title">
                                        <a href={article.url} target="_blank" rel="noopener noreferrer">
                                            {article.title}
                                        </a>
                                    </h3>
                                    <p className="news-summary">{article.summary}</p>
                                    <div className="news-footer">
                                        <span className="news-date">
                                            {formatDateTime(article.publishedAt)}
                                        </span>
                                        {article.author && (
                                            <span className="news-author">by {article.author}</span>
                                        )}
                                        <a
                                            href={article.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="news-read-more"
                                        >
                                            Read full article →
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default NewsTab;